/**
 * CopilotChatService
 *
 * Talks to the Connect-native assistant API (/vokalis/assistant/...).
 * Gemini + tool-calling runs server-side; the frontend never calls vokalis-backend.
 */

function normalizeConversation(conversation) {
  const id = conversation.id ?? conversation.sessionId ?? conversation.session_id ?? ''
  const lastMessageAt =
    conversation.updatedAt ||
    conversation.lastMessageAt ||
    conversation.updated_at ||
    conversation.createdAt ||
    conversation.created_at
  return {
    id: String(id || 'unknown'),
    title: conversation.title || 'Chat',
    lastMessageAt: lastMessageAt
      ? new Date(lastMessageAt).toISOString()
      : new Date().toISOString(),
  }
}

function normalizeMessage(msg, index) {
  const id = msg.id || msg.messageId || `msg-${index}-${Date.now()}`
  const role = (msg.role || 'assistant').toLowerCase()
  const content = msg.content || msg.text || msg.message || ''
  const createdAt = msg.createdAt || msg.created_at || new Date().toISOString()
  return {
    id: String(id),
    role: role === 'user' ? 'user' : role === 'assistant' ? 'assistant' : 'assistant',
    contentType: msg.contentType || 'markdown',
    content: typeof content === 'string' ? content : JSON.stringify(content),
    createdAt: typeof createdAt === 'string' ? createdAt : new Date(createdAt).toISOString(),
    metadata: msg.metadata || {},
  }
}

function getWorkspaceId(contextSnapshot) {
  return (
    contextSnapshot?.workspaceId ||
    contextSnapshot?.workspace_id ||
    null
  )
}

export default class CopilotChatService {
  constructor(client) {
    this.client = client
  }

  async listConversations(workspaceId, limit = 25) {
    const { data } = await this.client.get('/vokalis/assistant/conversations/', {
      params: { workspace_id: workspaceId, limit },
    })
    const list = Array.isArray(data) ? data : data?.sessions || data?.data || []
    return list.map(normalizeConversation)
  }

  async getConversation(conversationId) {
    const { data: messagesData } = await this.client.get(
      `/vokalis/assistant/conversations/${conversationId}/messages/`
    )
    const rawMessages = Array.isArray(messagesData)
      ? messagesData
      : messagesData?.messages || messagesData?.data || []
    const messages = rawMessages.map((m, i) => normalizeMessage(m, i))
    return { messages }
  }

  async startConversation(contextSnapshot) {
    const workspaceId = getWorkspaceId(contextSnapshot)
    if (!workspaceId) {
      throw new Error('workspaceId is required to start an assistant conversation')
    }
    const { data } = await this.client.post('/vokalis/assistant/conversations/', {
      workspace_id: workspaceId,
    })
    const id = data?.id || data?.sessionId || data?.session_id
    if (!id) {
      throw new Error('Backend did not return conversation id')
    }
    return { id: String(id) }
  }

  async sendUserMessage(conversationId, payload) {
    const workspaceId = getWorkspaceId(payload?.context)
    const body = {
      message: payload?.content || '',
      workspace_id: workspaceId,
      context: payload?.context || {},
    }
    if (payload?.clarificationChoice) {
      body.clarification_choice = payload.clarificationChoice
    }
    const { data } = await this.client.post(
      `/vokalis/assistant/conversations/${conversationId}/messages/`,
      body
    )
    if (data?.messages?.length) {
      return { messages: data.messages.map((m, i) => normalizeMessage(m, i)) }
    }
    if (data?.assistant_message) {
      const { messages } = await this.getConversation(conversationId)
      return { messages }
    }
    const { messages } = await this.getConversation(conversationId)
    return { messages }
  }

  async getConfig(workspaceId) {
    const { data } = await this.client.get('/vokalis/assistant/config/', {
      params: { workspace_id: workspaceId },
    })
    return data
  }

  connectStream(conversationId, handlers) {
    throw new Error('CopilotChatService.connectStream is not implemented yet.')
  }
}
